
import { useEffect, useRef, useState } from 'react'

const VIEWER_SCRIPT =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:4300/lex-iespv-viewer.js'
    : 'https://www.lighting.exchange/lex-iespv-viewer.js'

export default function PhotometryViewer({ product, fileId, _classes }) {
  const renderAreaRef = useRef(null)
  const viewerRef = useRef(null)

  const [scriptStatus, setScriptStatus] = useState('loading')
  const [photometryStatus, setPhotometryStatus] = useState('idle')
  const [photometry, setPhotometry] = useState([])
  const [selectedPhotometryId, setSelectedPhotometryId] = useState(null)

  /*
   * Load the external photometry viewer.
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    if (typeof window.IESPhotometricViewer === 'function') {
      console.log('IES Photometric Viewer is already available')

      setScriptStatus('loaded')
      return
    }

    let script = document.querySelector(
      `script[src="${VIEWER_SCRIPT}"]`
    )

    const handleLoad = () => {
      console.log('IES Photometric Viewer script loaded')

      if (typeof window.IESPhotometricViewer === 'function') {
        setScriptStatus('loaded')
      } else {
        console.error(
          'IES Photometric Viewer script loaded, but ' +
          'window.IESPhotometricViewer was not found'
        )

        setScriptStatus('error')
      }
    }

    const handleError = () => {
      console.error(
        `Unable to load IES Photometric Viewer from ${VIEWER_SCRIPT}`
      )

      setScriptStatus('error')
    }

    if (script) {
      console.log('IES Photometric Viewer script tag already exists')

      script.addEventListener('load', handleLoad)
      script.addEventListener('error', handleError)
    } else {
      console.log('Loading IES Photometric Viewer script')

      script = document.createElement('script')
      script.src = VIEWER_SCRIPT
      script.async = true

      script.addEventListener('load', handleLoad)
      script.addEventListener('error', handleError)

      document.head.appendChild(script)
    }

    return () => {
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
    }
  }, [])

  /*
   * Load the initial photometry list/data once the viewer script
   * is available.
   *
   * The first response is expected to look something like:
   *
   * [
   *   { id: 123, name: "Photometry A", data: {...} },
   *   { id: 456, name: "Photometry B" },
   *   { id: 789, name: "Photometry C" }
   * ]
   */
  useEffect(() => {
    if (scriptStatus !== 'loaded') {
      return
    }

    if (!product?.id) {
      console.error('PhotometryViewer: product.id is required')
      return
    }

    let cancelled = false

    const loadInitialPhotometry = async () => {
      setPhotometryStatus('loading')
      let params = ''
      if (fileId) {
        params=`?file_id=${fileId}`
      }

      try {
        const response = await fetch(
          `/lex-api/photometry/${encodeURIComponent(product.id)}${params}`,
          {
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/json'
            }
          }
        )

        if (!response.ok) {
          throw new Error(
            `Photometry request failed: ${response.status}`
          )
        }

        const data = await response.json()

        if (cancelled) {
          return
        }

        setPhotometry(data)

        /*
         * The first item contains the data needed for the initial
         * viewer render.
         */
        if (data.length > 0) {
          setSelectedPhotometryId(String(data[0].id))

          renderViewer(data[0].data)
        } else {
          setPhotometryStatus('loaded')
        }
      } catch (error) {
        if (cancelled) {
          return
        }

        console.error('Unable to load photometry:', error)
        setPhotometryStatus('error')
      }
    }

    loadInitialPhotometry()

    return () => {
      cancelled = true
    }
  }, [scriptStatus, product?.id])

  /*
   * Fetch and render a different photometric record when the
   * dropdown selection changes.
   */
  useEffect(() => {
    if (
      scriptStatus !== 'loaded' ||
      !product?.id ||
      !selectedPhotometryId ||
      photometry.length === 0
    ) {
      return
    }

    /*
     * Don't fetch the initial record again. It was already returned
     * with the initial request and has already been rendered.
     */
    if (String(photometry[0].id) === String(selectedPhotometryId)) {
      return
    }

    let cancelled = false

    const loadSelectedPhotometry = async () => {
      setPhotometryStatus('loading')

      try {
        const params = new URLSearchParams({
          photometry_id: selectedPhotometryId
        })

        if (fileId) {
          params.set('file_id', fileId)
        }
        const response = await fetch(
          `/lex-api/photometry/${encodeURIComponent(product.id)}?${params}`,
          {
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/json'
            }
          }
        )

        if (!response.ok) {
          throw new Error(
            `Photometry request failed: ${response.status}`
          )
        }

        const data = await response.json()

        if (cancelled) {
          return
        }

        /*
         * The selected request returns the photometric record/data.
         *
         * Adjust this if the endpoint returns the object directly
         * versus an array.
         */
        console.log(data)
        const photometricData = data.data || data

        renderViewer(photometricData)
      } catch (error) {
        if (cancelled) {
          return
        }

        console.error('Unable to load selected photometry:', error)
        setPhotometryStatus('error')
      }
    }

    loadSelectedPhotometry()

    return () => {
      cancelled = true
    }
  }, [
    scriptStatus,
    product?.id,
    selectedPhotometryId,
    photometry
  ])

  /*
   * Create/re-create the viewer.
   */
  const renderViewer = (data) => {
    if (!renderAreaRef.current) {
      return
    }

    /*
     * Clear the previous viewer before creating the new one.
     */
    renderAreaRef.current.innerHTML = ''

    viewerRef.current = new window.IESPhotometricViewer(
      renderAreaRef.current,
      data,
      {
        sections: product.photometry_options
      }
    )

    viewerRef.current.render()

    setPhotometryStatus('loaded')
  }

  /*
   * Clean up our reference when the component is removed.
   *
   * We intentionally do NOT remove the external script.
   */
  useEffect(() => {
    return () => {
      viewerRef.current = null
    }
  }, [])

  return (
    <div className={_classes}>
      {scriptStatus === 'loading' && (
        <div>Loading photometry viewer...</div>
      )}

      {scriptStatus === 'error' && (
        <div>
          Unable to load the photometry viewer.
        </div>
      )}
      <div className='lexcms-photometry-modal-header'> 
        <h4 className='lexcms-photometry-modal-title'>PHOTOMETRIC VIEWER </h4> 
        <h3 className='lexcms-photometry-modal-product-name'>{product.name }</h3> 
      </div>

      {scriptStatus === 'loaded' && photometry.length > 1 && (

        <div className="lexcms-photometry-viewer-dropdown">
          <label>Select A File: </label>
          <select
            id="photometry-select"
            value={selectedPhotometryId || ''}
            onChange={(event) =>
              setSelectedPhotometryId(event.target.value)
            }
            disabled={photometryStatus === 'loading'}
          >
            {photometry.map((item) => (
              <option
                key={item.id}
                value={item.id}
              >
                {item.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {scriptStatus === 'loaded' &&
        photometryStatus === 'loading' && (
          <div>Loading photometry...</div>
        )}

      {scriptStatus === 'loaded' &&
        photometryStatus === 'error' && (
          <div>
            Unable to load photometry.
          </div>
        )}

      <div ref={renderAreaRef} />
    </div>
  )
}
